import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '../../context/AppContext'
import { TICKET_LIST_COLUMNS } from '../../services/ticketService'

/**
 * Mapeo de estados de los tickets.
 * @constant {Object}
 */
const STATUS_MAP = {
  1: { label: 'Abierto', cls: 'text-bg-warning' },
  2: { label: 'En progreso', cls: 'text-bg-primary' },
  3: { label: 'Resuelto', cls: 'text-bg-success' },
  4: { label: 'Cerrado', cls: 'text-bg-secondary' },
}

/**
 * Paleta de colores para avatares generados por iniciales.
 * @constant {string[]}
 */
const AVATAR_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#ec4899',
  '#f59e0b',
  '#10b981',
  '#3b82f6',
  '#ef4444',
  '#14b8a6',
]

/**
 * Obtiene las iniciales de un usuario a partir de su nombre y apellido.
 * @param {string} [nombre='']
 * @param {string} [apellido='']
 * @returns {string} Iniciales en mayúsculas.
 */
const getInitials = (nombre = '', apellido = '') =>
  `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase()

/**
 * Determina un color de la paleta basándose en el código de carácter del nombre.
 * @param {string} [nombre='']
 * @returns {string} Código de color hexadecimal.
 */
const getAvatarColor = (nombre = '') =>
  AVATAR_COLORS[nombre.charCodeAt(0) % AVATAR_COLORS.length]

/**
 * Componente funcional que renderiza la imagen de perfil del usuario o un fallback con iniciales.
 * @component
 * @param {Object} props
 * @param {string} props.photo - URL de la imagen.
 * @param {string} props.nombre - Nombre del usuario.
 * @param {string} props.apellido - Apellido del usuario.
 */
const Avatar = ({ photo, nombre, apellido }) => {
  const [imgError, setImgError] = useState(false)

  if (!photo || imgError) {
    return (
      <div
        className="rounded-circle d-flex align-items-center justify-content-center"
        style={{
          width: 80,
          height: 80,
          background: getAvatarColor(nombre),
          fontSize: '1.75rem',
          fontWeight: 600,
          color: '#fff',
          userSelect: 'none',
          flexShrink: 0,
          border: '2px solid rgba(255,255,255,0.15)',
        }}
      >
        {getInitials(nombre, apellido)}
      </div>
    )
  }

  return (
    <img
      src={photo}
      alt={`${nombre} ${apellido}`}
      className="rounded-circle"
      style={{ width: 80, height: 80, objectFit: 'cover', flexShrink: 0 }}
      onError={() => setImgError(true)}
    />
  )
}

/**
 * Definición de campos de información para el renderizado dinámico en el perfil.
 * @constant {Array<[string, string]>}
 */
const INFO_FIELDS = [
  ['Código', 'codigo_empleado'],
  ['Email', 'email'],
  ['Teléfono', 'telefono'],
  ['Rol', 'rol'],
]

/**
 * Vista detallada de un usuario que incluye información de perfil y tickets relacionados.
 * * **Características**:
 * - Navegación entre pestañas de "Perfil" y "Tickets".
 * - Consulta automática a Supabase de tickets asignados y creados.
 * - Estadísticas rápidas de tickets abiertos.
 * - Soporte para temas de Bootstrap 5.
 * * @component
 * @param {Object} props
 * @param {Object} props.user - Objeto del usuario a visualizar.
 * @param {string|number} props.user.id - UUID o ID del usuario.
 * @param {string} props.user.nombre - Nombre del usuario.
 * @param {string} props.user.apellido - Apellido del usuario.
 * @param {boolean} props.user.activo - Estado de cuenta del usuario.
 * @param {string} [props.user.photo] - URL de la foto de perfil.
 */
const UserView = ({ user }) => {
  const navigate = useNavigate()
  const { supabase } = useApp()
  const [assigned, setAssigned] = useState([]) // tickets donde es responsable
  const [created, setCreated] = useState([]) // tickets creados por el usuario
  const [loading, setLoading] = useState(false)
  const [tab, setTab] = useState('info') // 'info' | 'tickets'

  /**
   * Carga en paralelo los tickets asignados (responsable) y los creados por el
   * usuario. Se hace en una sola pasada para evitar condiciones de carrera.
   */
  useEffect(() => {
    if (!user?.id) return

    let active = true
    const fetchAll = async () => {
      setLoading(true)
      try {
        const [assignedRes, createdRes] = await Promise.all([
          supabase
            .schema('Tickets')
            .from('ticket')
            .select(TICKET_LIST_COLUMNS)
            .eq('responsibleId', user.id)
            .eq('deleted', false),
          supabase
            .schema('Tickets')
            .from('ticket')
            .select(TICKET_LIST_COLUMNS)
            .eq('userId', user.id)
            .eq('deleted', false),
        ])

        if (!active) return
        setAssigned(assignedRes.error ? [] : assignedRes.data || [])
        setCreated(createdRes.error ? [] : createdRes.data || [])
      } finally {
        if (active) setLoading(false)
      }
    }

    fetchAll()
    return () => {
      active = false
    }
  }, [user, supabase])

  if (!user) return <div className="alert alert-warning">No hay usuario</div>

  // KPIs derivados de los tickets del usuario
  const openTickets = assigned.filter(
    (t) => !t.resolvedAt && !t.closedAt && !t.deleted && !t.completed
  )
  const resolvedTickets = assigned.filter((t) => t.completed || t.resolvedAt)

  const kpis = [
    { label: 'Asignados', value: assigned.length, cls: 'text-primary' },
    { label: 'Abiertos', value: openTickets.length, cls: 'text-warning' },
    { label: 'Resueltos', value: resolvedTickets.length, cls: 'text-success' },
    { label: 'Creados', value: created.length, cls: 'text-info' },
  ]

  return (
    <div
      className="card border-0 shadow-sm overflow-hidden"
      style={{ maxWidth: 400 }}
    >
      {/* ── Header ── */}
      <div
        className="card-header text-center border-0 pb-0"
        style={{ background: 'var(--bs-tertiary-bg)' }}
      >
        <div className="d-flex justify-content-center mt-3 mb-2">
          <Avatar
            photo={user.photo}
            nombre={user.nombre}
            apellido={user.apellido}
          />
        </div>

        <h5 className="mb-1 fw-semibold">
          {user.nombre} {user.apellido}
        </h5>

        <span
          className={`badge rounded-pill mb-3 ${user.activo ? 'text-bg-success' : 'text-bg-danger'}`}
          style={{ fontSize: '0.7rem', letterSpacing: '0.3px' }}
        >
          {user.activo ? 'Activo' : 'Inactivo'}
        </span>

        {/* Tabs */}
        <ul
          className="nav nav-tabs border-0 justify-content-center mt-1"
          style={{ marginBottom: '-1px' }}
        >
          {[
            ['info', 'Perfil'],
            ['tickets', `Tickets (${assigned.length})`],
          ].map(([key, label]) => (
            <li key={key} className="nav-item">
              <button
                className={`nav-link border-0 px-4 ${tab === key ? 'active fw-semibold' : 'text-body-secondary'}`}
                onClick={() => setTab(key)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* ── Tab: Perfil ── */}
      {tab === 'info' && (
        <div className="card-body px-4 pt-3 pb-4">
          <dl className="mb-3" style={{ fontSize: '0.875rem' }}>
            {INFO_FIELDS.map(([label, key]) => (
              <div
                key={key}
                className="d-flex justify-content-between py-2 border-bottom"
              >
                <dt className="fw-normal text-body-secondary">{label}</dt>
                <dd
                  className="mb-0 fw-semibold text-end"
                  style={{ maxWidth: '60%', wordBreak: 'break-all' }}
                >
                  {user[key] || '—'}
                </dd>
              </div>
            ))}
          </dl>

          {/* KPIs */}
          <div className="row g-2 mt-1">
            {kpis.map((kpi) => (
              <div key={kpi.label} className="col-6">
                <div
                  className="rounded-3 text-center py-3"
                  style={{ background: 'var(--bs-tertiary-bg)' }}
                >
                  <div
                    className="text-body-secondary mb-1"
                    style={{ fontSize: '0.75rem' }}
                  >
                    {kpi.label}
                  </div>
                  <div
                    className={`fw-semibold ${kpi.cls}`}
                    style={{ fontSize: '1.6rem', lineHeight: 1 }}
                  >
                    {loading ? '…' : kpi.value}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Tab: Tickets ── */}
      {tab === 'tickets' && (
        <div className="card-body p-0">
          {loading && (
            <p className="text-center text-body-secondary py-4 small">
              Cargando tickets…
            </p>
          )}

          {!loading && assigned.length === 0 && (
            <p className="text-center text-body-secondary py-4 small">
              Sin tickets asignados
            </p>
          )}

          {!loading && assigned.length > 0 && (
            <ul className="list-group list-group-flush">
              {assigned.map((ticket) => {
                const status = STATUS_MAP[ticket.statusId] ?? {
                  label: 'Desconocido',
                  cls: 'text-bg-secondary',
                }
                const date = new Date(ticket.createdAt).toLocaleDateString(
                  'es-CR',
                  {
                    day: '2-digit',
                    month: 'short',
                  }
                )
                return (
                  <li
                    key={ticket.id}
                    onClick={() => navigate(`/ticket/${ticket.id}`)}
                    className=" d-flex align-items-center gap-3 px-4 py-3"
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="flex-grow-1 overflow-hidden">
                      <p
                        className="mb-0 fw-semibold text-truncate"
                        style={{ fontSize: '0.875rem' }}
                      >
                        {ticket.title}
                      </p>
                      <p
                        className="mb-0 text-body-secondary"
                        style={{ fontSize: '0.75rem' }}
                      >
                        {ticket.systemOrigin} · {date}
                      </p>
                    </div>
                    <span
                      className={`badge rounded-pill flex-shrink-0 ${status.cls}`}
                      style={{ fontSize: '0.7rem' }}
                    >
                      {status.label}
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default UserView
